import { useCallback, useEffect, useState } from "react";
import TableButton from "components/atoms/buttons/TableButton";
import { EmailPresenter } from "./EmailPresenter";

import * as s from "./EmailStyled";
import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { deleteEmail, getEmailList, postEmail } from "api/emailAPI";
import { AddEmail, Email } from "api/interfaces/emailInterface.interface";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { deleteHtmlTag, showCount } from "utils/functions";
import { getAccountTimeFormat } from "utils/timeUtil";
import { putEmail } from "api/emailAPI";
import { Location, useLocation } from "react-router-dom";
import { isNull } from "lodash";
import { TableColumnsType } from "antd";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { useMenu } from "hook/useMenu";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { ColumnType } from "antd/es/table";
import { useIsMobile } from "components/MediaQuery";
import useApiError from "hook/useApiError";

export enum EMAIL_PAGE_TABS {
  SENT_EMAILS,
  SCHEDULED_EMAILS,
}

const initialQueryInfo = {
  ...baseQueryInfo,
  sortDirection: SORT_DIRECTION.DESC,
  sortType: "subject",
};

export default function EmailContainer(): JSX.Element {
  const location: Location = useLocation();

  const [isAddModal, setIsAddModal] = useState(false);
  const [isEditModal, setIsEditModal] = useState(false);
  const [isViewModal, setIsViewModal] = useState(false);
  const isMobile = useIsMobile();
  const [currentMenu, setCurrentMenu] = useState<EMAIL_PAGE_TABS>(
    EMAIL_PAGE_TABS.SENT_EMAILS
  );
  const queryClient = useQueryClient();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const { menuNavigation, queryNavigate } = useMenu({});
  const { lang, notifySuccess, notifyError } = useApiError();

  const emailMenus = [
    { label: "Sent Emails", value: EMAIL_PAGE_TABS.SENT_EMAILS.toString() },
    {
      label: "Scheduled Emails",
      value: EMAIL_PAGE_TABS.SCHEDULED_EMAILS.toString(),
    },
  ];

  const onChangeCurrentMenu = (value: string) => {
    setCurrentMenu(Number(value) as unknown as EMAIL_PAGE_TABS);
  };

  useEffect(() => {
    const changeParam = new URLSearchParams(location.search);
    if (changeParam.size > 0) {
      setEmailQueryInfo((info) => {
        return {
          ...info,
          pageNum: isNull(changeParam.get("pageNum"))
            ? 0
            : Number(changeParam.get("pageNum")),
          pageLimit: isNull(changeParam.get("pageLimit"))
            ? 20
            : Number(changeParam.get("pageLimit")),
          keyword: isNull(changeParam.get("keyword"))
            ? ""
            : (changeParam.get("keyword") as string),
          sortType: isNull(changeParam.get("sortType"))
            ? "subject"
            : (changeParam.get("sortType") as string),
          sortDirection: isNull(changeParam.get("sortDirection"))
            ? SORT_DIRECTION.DESC
            : (changeParam.get("sortDirection") as SORT_DIRECTION),
        };
      });
    } else {
      setEmailQueryInfo((info) => {
        return {
          ...info,
          pageNum: 0,
          pageLimit: 20,
          keyword: "",
          sortType: "subject",
          sortDirection: SORT_DIRECTION.DESC,
        };
      });
    }
  }, [location]);

  const mutationDeleteEmail = useMutation(deleteEmail, {
    onSuccess: () => {
      if (currentMenu === EMAIL_PAGE_TABS.SENT_EMAILS) {
        notifySuccess(lang.label.setting.email.notify.delete.success);
      }
      if (currentMenu === EMAIL_PAGE_TABS.SCHEDULED_EMAILS) {
        notifySuccess(lang.label.setting.email.notify.schedule.delete.success);
      }
      queryClient.invalidateQueries("email");
    },
    onError: () => {
      if (currentMenu === EMAIL_PAGE_TABS.SENT_EMAILS) {
        notifyError(lang.label.setting.email.notify.delete.fail);
      }
      if (currentMenu === EMAIL_PAGE_TABS.SCHEDULED_EMAILS) {
        notifyError(lang.label.setting.email.notify.schedule.delete.fail);
      }
    },
  });

  const onDeleteEmail = (emailId: string) => {
    mutationDeleteEmail.mutate({
      accountId: selectedAccount.accountId,
      emailId: emailId,
    });
  };

  const onDetailRecipients = (row: Email) => {
    setSelectedEmail(row);
    menuNavigation({
      to: `/manager/emails/recipients/${row.subject}/${row.emailId}`,
    });
  };

  const onModal = (type?: string) => {
    if (type === "add") {
      setIsAddModal(!isAddModal);
    }
    if (type === "edit") {
      setIsEditModal(!isEditModal);
    }
    if (type === "view") {
      setIsViewModal(!isViewModal);
    }
  };

  // row 전체 클릭 시
  const onRowClicked = (row: Email) => {
    if (currentMenu === EMAIL_PAGE_TABS.SENT_EMAILS) {
      onModal("view");
    }
    if (currentMenu === EMAIL_PAGE_TABS.SCHEDULED_EMAILS) {
      onModal("edit");
    }
    setSelectedEmail(row);
  };

  const [emails, setEmails] = useState<Email[]>([]);
  const [scheduleEmails, setScheduleEmails] = useState<Email[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<Email>();

  const [pending, setPending] = useState<boolean>(true);

  const [emailQueryInfo, setEmailQueryInfo] = useState<BasePagination>({
    ...initialQueryInfo,
  });
  const queryKey = useQueryKey(emailQueryInfo);

  const [status, setStatus] = useState({
    totalTitle: "Total",
    totalValue: 0,
  });

  const onHeaderCell = (column: ColumnType<Email>) => {
    return {
      onClick: () => {
        if (column.key) {
          setEmailQueryInfo((info) => {
            return {
              ...info,
              sortType: column.key as string,
              sortDirection: onSortAntd(info.sortDirection),
              pageNum: 0,
            };
          });
        }
      },
    };
  };

  //temp columns
  const columns: TableColumnsType<Email> = [
    {
      title: "Subject",
      sorter: true, // 정렬 활성화,
      key: "subject",
      render: (value, row) => row.subject,
      sortOrder: changeSortOrderAntd(emailQueryInfo, "subject"),
      defaultSortOrder: "descend",
      onHeaderCell: onHeaderCell,
    },
    {
      title: "Recipients",
      render: (value, row) => {
        if (row.recipientType === "USER")
          return showCount((row.users ?? []).map((user) => user.name));
        else return row.recipientType;
      },
      width: currentMenu === EMAIL_PAGE_TABS.SENT_EMAILS ? "20%" : 200,
      responsive: ["lg"],
    },
    {
      title: "Message",
      render: (value, row) => deleteHtmlTag(row.message),
      width: currentMenu === EMAIL_PAGE_TABS.SENT_EMAILS ? "30%" : "50%",
      responsive: ["xxl"],
    },
    {
      title: "Open Rate",
      render: (value, row) => `${row.openRate}%`,
      align: "center",
      responsive: ["md"],
      width: 80,
      hidden: currentMenu !== EMAIL_PAGE_TABS.SENT_EMAILS,
    },
    {
      key: "createTime",
      title: "Send Time",
      sorter: true,
      render: (value, row) =>
        row.scheduleTime === undefined
          ? getAccountTimeFormat(row.sendTime, selectedAccount, true)
          : getAccountTimeFormat(row.scheduleTime, selectedAccount, true),
      sortOrder: changeSortOrderAntd(emailQueryInfo, "createTime"),
      onHeaderCell: onHeaderCell,
      responsive: ["md"],
      width: 200,
      hidden: currentMenu !== EMAIL_PAGE_TABS.SENT_EMAILS,
    },
    {
      dataIndex: "scheduleTime",
      title: "Scheduled Send Time",
      sorter: true,
      key: "scheduleTime",
      sortOrder: changeSortOrderAntd(emailQueryInfo, "scheduleTime"),
      onHeaderCell: onHeaderCell,
      render: (value, row) =>
        row.scheduleTime !== undefined
          ? getAccountTimeFormat(row.scheduleTime, selectedAccount, true)
          : "N/A",
      hidden: currentMenu === EMAIL_PAGE_TABS.SENT_EMAILS,
      width: 200,
    },
    // 마지막 버튼
    {
      title: "",
      width: isMobile ? 200 : 340,
      render: (value, row, index) => (
        <s.Row className="wrapper">
          <TableButton
            buttonType="warning"
            label="Delete"
            onClick={() => onDeleteEmail(row.emailId)}
            data-testid={`manager-email-delete-email-${index}`}
          />

          <TableButton
            label="View Recipients"
            onClick={(e) => {
              e.stopPropagation();

              onDetailRecipients(row);
            }}
            data-testid={`manager-email-view-recipients-${index}`}
          />

          <span className="row-click-button">
            <TableButton
              label="Open"
              onClick={() => onRowClicked(row)}
              data-testid={`manager-email-open-${index}`}
            />
          </span>
        </s.Row>
      ),

      align: "right",
      hidden: currentMenu !== EMAIL_PAGE_TABS.SENT_EMAILS,
    },
    // 마지막 버튼
    {
      title: "",
      width: isMobile ? 120 : 160,
      render: (value, row, index) => (
        <s.Row className="wrapper">
          <TableButton
            buttonType="warning"
            label="Delete"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteEmail(row.emailId);
            }}
            data-testid={`manager-email-delete-${index}`}
          />

          <span className="row-click-button">
            <TableButton
              label="Edit"
              onClick={() => onRowClicked(row)}
              data-testid={`manager-email-edit-${index}`}
            />
          </span>
        </s.Row>
      ),

      align: "right",
      hidden: currentMenu === EMAIL_PAGE_TABS.SENT_EMAILS,
    },
  ];

  const { refetch } = useQuery(
    ["email", queryKey, currentMenu],
    () =>
      getEmailList({
        isSchedule: currentMenu === EMAIL_PAGE_TABS.SENT_EMAILS ? false : true,
        payload: emailQueryInfo,
        accountId: selectedAccount.accountId,
      }),
    {
      retry: 3,
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        if (res.error !== 0) {
          if (currentMenu === EMAIL_PAGE_TABS.SENT_EMAILS) {
            setEmails([]);
          }
          if (currentMenu === EMAIL_PAGE_TABS.SCHEDULED_EMAILS) {
            setScheduleEmails([]);
          }
          setStatus((info) => {
            return {
              ...info,
              totalValue: 0,
            };
          });
          return;
        }
        if (currentMenu === EMAIL_PAGE_TABS.SENT_EMAILS) {
          setEmails(
            res.result.map((email: Email) => ({
              ...email,
              key: email.emailId,
            }))
          );
        }
        if (currentMenu === EMAIL_PAGE_TABS.SCHEDULED_EMAILS) {
          setScheduleEmails(
            res.result.map((email: Email) => ({
              ...email,
              key: email.emailId,
            }))
          );
        }
        setStatus((info) => {
          return {
            ...info,
            totalValue: res.page.total,
          };
        });
      },
      onError: (e: any) => {
        if (currentMenu === EMAIL_PAGE_TABS.SENT_EMAILS) {
          setEmails([]);
        }
        if (currentMenu === EMAIL_PAGE_TABS.SCHEDULED_EMAILS) {
          setScheduleEmails([]);
        }
        setStatus((info) => {
          return {
            ...info,
            totalValue: 0,
          };
        });
      },
      onSettled: (e: any) => {
        setPending(false);
      },
    }
  );

  const mutationCreateEmail = useMutation(postEmail, {
    onSuccess: () => {
      if (currentMenu === EMAIL_PAGE_TABS.SENT_EMAILS) {
        notifySuccess(lang.label.common.notify.email.success);
      }
      if (currentMenu === EMAIL_PAGE_TABS.SCHEDULED_EMAILS) {
        notifySuccess(lang.label.common.notify.email.success);
      }
      queryClient.invalidateQueries("email");
      setIsAddModal(false);
    },
    onError: () => {
      if (currentMenu === EMAIL_PAGE_TABS.SENT_EMAILS) {
        notifyError(lang.ServerErrorCode[121014]);
      }
      if (currentMenu === EMAIL_PAGE_TABS.SCHEDULED_EMAILS) {
        notifyError(lang.ServerErrorCode[121014]);
      }
    },
  });

  const mutationUpdateEmail = useMutation(putEmail, {
    onSuccess: () => {
      notifySuccess(lang.label.email.notify.update.success);
      queryClient.invalidateQueries("email");
      setIsEditModal(false);
    },
    onError: () => {
      notifyError(lang.label.email.notify.update.fail);
    },
  });

  const onClickSendEmail = useCallback(
    (email: AddEmail, emailId?: string) => {
      if (email.recipientType !== "USER") {
        delete email.users;
      }

      if (emailId) {
        mutationUpdateEmail.mutate({
          accountId: selectedAccount.accountId,
          emailId: emailId,
          payload: email,
        });
      } else {
        mutationCreateEmail.mutate({
          accountId: selectedAccount.accountId,
          payload: email,
        });
      }
    },
    [mutationCreateEmail]
  );

  const onChangePage = (page: number, totalRows: number) => {
    // console.log(page, totalRows);
    if (page !== emailQueryInfo.pageNum + 1) {
      queryNavigate({
        to: location.pathname,
        queryInfo: {
          pageLimit: emailQueryInfo.pageLimit,
          pageNum: page - 1,
          keyword: emailQueryInfo.keyword as string,
          sortType: emailQueryInfo.sortType,
          sortDirection: emailQueryInfo.sortDirection as SORT_DIRECTION,
        },
      });
      setPending(true);
      // setEmailQueryInfo((info) => {
      //   return { ...info, pageNum: (page - 1) as number };
      // });
    }
  };

  const onClickSearch = useCallback(
    (keyword: string) => {
      if (emailQueryInfo.keyword !== keyword) {
        queryNavigate({
          to: location.pathname,
          queryInfo: {
            pageLimit: emailQueryInfo.pageLimit,
            pageNum: 0,
            keyword: keyword,
            sortType: emailQueryInfo.sortType,
            sortDirection: emailQueryInfo.sortDirection as SORT_DIRECTION,
          },
        });
      } else {
        refetch();
      }
      setPending(true);
      // setEmailQueryInfo((query) => {
      //   return {
      //     ...query,
      //     keyword,
      //   };
      // });
      // refetch();
    },
    [
      emailQueryInfo.keyword,
      emailQueryInfo.pageLimit,
      emailQueryInfo.sortDirection,
      emailQueryInfo.sortType,
      location.pathname,
      refetch,
      queryNavigate,
    ]
  );

  return (
    <EmailPresenter
      onModal={onModal}
      isAddModal={isAddModal}
      isEditModal={isEditModal}
      isViewModal={isViewModal}
      columns={columns}
      data={
        currentMenu === EMAIL_PAGE_TABS.SENT_EMAILS ? emails : scheduleEmails
      }
      currentMenu={currentMenu}
      emailMenus={emailMenus}
      onChangeCurrentMenu={onChangeCurrentMenu}
      onClickSendEmail={onClickSendEmail}
      selectedEmail={selectedEmail}
      onRowClicked={onRowClicked}
      onChangePage={onChangePage}
      paginationInfo={emailQueryInfo}
      status={status}
      onClickSearch={onClickSearch}
      tablePending={pending}
    />
  );
}
